import { Command } from 'cmdk'
import { cn } from '@/lib/utils/cn'

export interface ComboboxItem {
  value: string
  label: React.ReactNode
  /** Secondary line shown right-aligned (monospace) — e.g. part number, code. */
  meta?: string
  /** Optional second line under the label — e.g. INCI / family / stock. */
  description?: string
  /** Optional keywords that also match the search input. */
  keywords?: string[]
  /** Logical group. Multiple items with the same group render under one header. */
  group?: string
  disabled?: boolean
}

export interface ComboboxProps {
  items: ComboboxItem[]
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  emptyMessage?: string
  /** Optional className on the root command panel. */
  className?: string
  /**
   * Controlled input value. Pair with `onInputChange` for server-side
   * search — the consumer owns the query string and re-supplies `items`
   * from the endpoint. When omitted, cmdk's uncontrolled input is used
   * and client-side fuzzy filtering runs automatically.
   */
  inputValue?: string
  onInputChange?: (value: string) => void
  /**
   * When `false`, cmdk's internal fuzzy filter is disabled — all `items`
   * render regardless of input text. Set this when `inputValue` is
   * controlled (server-search) so items aren't hidden by both the server
   * filter AND cmdk's client filter. Defaults to `true`.
   */
  shouldFilter?: boolean
}

/**
 * Inline search-driven picker with optional grouped results.
 * Built on cmdk for fuzzy matching + keyboard navigation (↑↓, Enter, Esc).
 *
 * For an input-first triggered version where the visible field IS the
 * search input (results float in a popover), use `SearchSelect`, which
 * composes its own `Command` + `ComboboxList`.
 */
export function Combobox({
  items,
  value,
  onChange,
  placeholder = 'Search…',
  emptyMessage = 'No results',
  className,
  inputValue,
  onInputChange,
  shouldFilter = true,
}: ComboboxProps) {
  return (
    <Command
      shouldFilter={shouldFilter}
      className={cn(
        'max-w-80 overflow-hidden rounded-md border border-border-default bg-surface shadow-md',
        className,
      )}
    >
      <Command.Input
        placeholder={placeholder}
        value={inputValue}
        onValueChange={onInputChange}
        className={cn(
          'w-full border-none bg-surface px-3 py-2 text-md text-primary outline-none',
          'border-b border-border-subtle placeholder:text-tertiary',
        )}
      />
      <ComboboxList
        items={items}
        value={value}
        onChange={onChange}
        emptyMessage={emptyMessage}
      />
    </Command>
  )
}

export interface ComboboxListProps {
  items: ComboboxItem[]
  value?: string
  onChange?: (value: string) => void
  emptyMessage?: string
  /** Optional className on the scrolling list container. */
  className?: string
}

/**
 * The grouped results list — `Command.List` + `Command.Empty` + grouped
 * `Command.Item`s. Must be rendered inside a cmdk `<Command>` ancestor
 * (React-tree-wise); the DOM node may live in a portal (e.g. inside a
 * `PopoverContent`) and cmdk navigation still works because it resolves
 * items via `document.getElementById`. Shared by `Combobox` (inline) and
 * `SearchSelect` (floating).
 */
export function ComboboxList({
  items,
  value,
  onChange,
  emptyMessage = 'No results',
  className,
}: ComboboxListProps) {
  const groups = groupItems(items)

  return (
    <Command.List className={cn('max-h-72 overflow-y-auto py-1', className)}>
      <Command.Empty className="px-3 py-5 text-center text-sm text-tertiary">
        {emptyMessage}
      </Command.Empty>
      {groups.map(({ group, items: groupItems }) => (
        <Command.Group
          key={group ?? '_'}
          heading={group}
          className={cn(
            '[&_[cmdk-group-heading]]:border-b [&_[cmdk-group-heading]]:border-border-subtle',
            '[&_[cmdk-group-heading]]:bg-surface-sunk [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5',
            '[&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-semibold',
            '[&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest',
            '[&_[cmdk-group-heading]]:text-tertiary',
          )}
        >
          {groupItems.map((item) => (
            <Command.Item
              key={item.value}
              value={item.value}
              keywords={item.keywords}
              disabled={item.disabled}
              onSelect={() => onChange?.(item.value)}
              className={cn(
                'grid cursor-pointer grid-cols-[1fr_auto] items-center gap-2.5 px-3 py-2 text-md',
                'data-[selected=true]:bg-surface-hover',
                value === item.value && 'bg-accent-subtle text-accent-text',
                'aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
              )}
            >
              {item.description ? (
                <span className="flex min-w-0 flex-col">
                  <span>{item.label}</span>
                  <span className="truncate text-xs text-tertiary">{item.description}</span>
                </span>
              ) : (
                <span>{item.label}</span>
              )}
              {item.meta ? (
                <span className="text-xs text-tertiary">{item.meta}</span>
              ) : null}
            </Command.Item>
          ))}
        </Command.Group>
      ))}
    </Command.List>
  )
}

function groupItems(items: ComboboxItem[]): { group: string | undefined; items: ComboboxItem[] }[] {
  const map = new Map<string | undefined, ComboboxItem[]>()
  for (const item of items) {
    const key = item.group
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(item)
  }
  return Array.from(map, ([group, items]) => ({ group, items }))
}
